/**
 * Temas de cor do portal (sai-carta-servico).
 *
 * O portal aplica o tema como atributo `data-theme` no `<html>` e troca as variáveis
 * `--primary-*` / `--accent-*` em `src/css/themes.css`. Aqui ficam apenas a lista de temas
 * e o tema publicado, para as telas do Editor da Homepage mostrarem a prévia com a cara
 * que o portal realmente tem.
 *
 * Quem grava o tema é o bloco "Padrão de Cores e Ordenação"; o catálogo abaixo é do
 * sistema e fica aqui, porque a API guarda só a chave escolhida pelo órgão.
 * `TEMA_PORTAL_ATUAL` é o tema que vale enquanto o órgão não escolhe o seu.
 */

/** Chave do tema, igual ao valor de `data-theme` no portal. */
export type TemaPortal =
  | 'blue'
  | 'ciano'
  | 'green'
  | 'red'
  | 'yellow'
  | 'pink'
  | 'purple'
  | 'grey';

export interface OpcaoTemaPortal {
  chave: TemaPortal;
  rotulo: string;
  /** Cor de destaque do tema no portal (`--accent-color`): botões, ícones e links. */
  corHex: string;
  /** Primeira parada do gradiente do cabeçalho (`--primary-dark`). */
  corFundo: string;
  /** Parada do meio do gradiente (`--primary-deep`). */
  corProfundo: string;
  /** Última parada do gradiente (`--primary-night`). */
  corNoite: string;
  /** Cor do texto sobre o fundo do tema (`--surface-ink`). */
  corTinta: string;
}

/**
 * Temas disponíveis no portal, com as cores de `src/css/themes.css` do sai-carta-servico.
 * Mudou o tema lá, atualize aqui e o bloco `.portal-tema` de `src/styles.css`.
 */
export const TEMAS_PORTAL: OpcaoTemaPortal[] = [
  { chave: 'blue', rotulo: 'Azul', corHex: '#2b5d96', corFundo: '#071a34', corProfundo: '#142543', corNoite: '#0b2446', corTinta: '#ffffff' },
  { chave: 'ciano', rotulo: 'Ciano', corHex: '#20c7d8', corFundo: '#072d34', corProfundo: '#143d43', corNoite: '#0b4645', corTinta: '#ffffff' },
  { chave: 'green', rotulo: 'Verde', corHex: '#2fa84f', corFundo: '#06300f', corProfundo: '#0e3913', corNoite: '#0b461a', corTinta: '#ffffff' },
  { chave: 'red', rotulo: 'Vermelho', corHex: '#e21b2d', corFundo: '#340707', corProfundo: '#431415', corNoite: '#460b0c', corTinta: '#ffffff' },
  { chave: 'yellow', rotulo: 'Amarelo', corHex: '#f4c542', corFundo: '#f9cb33', corProfundo: '#ffc832', corNoite: '#f4cd34', corTinta: '#3f3f3f' },
  { chave: 'pink', rotulo: 'Rosa', corHex: '#e6007e', corFundo: '#340721', corProfundo: '#43143e', corNoite: '#460b34', corTinta: '#ffffff' },
  { chave: 'purple', rotulo: 'Roxo', corHex: '#7c5cff', corFundo: '#230734', corProfundo: '#311443', corNoite: '#2c0b46', corTinta: '#ffffff' },
  { chave: 'grey', rotulo: 'Cinza', corHex: '#737373', corFundo: '#1f1f1f', corProfundo: '#2e2e2e', corNoite: '#292929', corTinta: '#ffffff' }
];

/** Gradiente do cabeçalho do tema, para a amostra da paleta. */
export function gradienteDoTema(tema: OpcaoTemaPortal): string {
  return `linear-gradient(100deg, ${tema.corFundo} 6.17%, ${tema.corProfundo} 51.75%, ${tema.corNoite} 93.83%)`;
}

/** Tema do sistema: o que o portal publica enquanto o órgão não escolhe o seu. */
export const TEMA_PORTAL_ATUAL: TemaPortal = 'blue';

/** Resolve a opção do catálogo; cai no tema azul quando a chave não é conhecida. */
export function encontrarTemaPortal(chave: TemaPortal | string): OpcaoTemaPortal {
  return TEMAS_PORTAL.find((tema) => tema.chave === chave) ?? TEMAS_PORTAL[0];
}

/**
 * Chave de tema vinda da API. Nula é o órgão que ainda não escolheu, e chave fora do
 * catálogo é tema que o portal não sabe renderizar: nos dois casos vale o do sistema.
 */
export function resolverTemaPortal(chave: string | null | undefined): TemaPortal {
  return TEMAS_PORTAL.find((tema) => tema.chave === chave)?.chave ?? TEMA_PORTAL_ATUAL;
}
